import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Paper,
  Typography,
  Divider,
  makeStyles,
} from "@material-ui/core";
import ChatIcon from "@material-ui/icons/Chat";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  container: {
    position: "fixed",
    bottom: 20,
    right: 20,
    zIndex: 1300,
  },
  paper: {
    width: 340,
    height: 460,
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1),
    borderBottom: "1px solid #ddd",
    backgroundColor: theme.UpdateTheme.button_color,
    color: theme.UpdateTheme.text_color,
  },
  headerIcon: {
    color: "#fff",
  },
  messageBox: {
    flex: 1,
    padding: theme.spacing(1),
    overflow: "auto",
  },
  userMessage: {
    alignSelf: "flex-end",
    backgroundColor: "#DCF8C6",
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
    borderRadius: 8,
    maxWidth: "98%",
  },
  assistantMessage: {
    alignSelf: "flex-start",
    backgroundColor: "#F1F0F0",
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
    borderRadius: 8,
    maxWidth: "98%",
  },
  messageText: {
    whiteSpace: "pre-wrap",
  },
  imagePreview: {
    maxWidth: "100%",
    borderRadius: 4,
    marginTop: 4,
    border: "1px solid #ccc",
  },
  pdfLink: {
    cursor: "pointer",
    color: "#1976d2",
  },
  pageLabel: {
    display: "block",
    marginTop: 2,
  },
  typingIndicator: {
    backgroundColor: "#F1F0F0",
    padding: theme.spacing(1),
    borderRadius: 8,
    margin: theme.spacing(0.5),
  },
  inputContainer: {
    display: "flex",
    padding: theme.spacing(1),
    borderTop: "1px solid #ddd",
  },
  inputField: {
    height: 35,
    fontSize: 15,
  },
  sendButton: {
    backgroundColor: theme.UpdateTheme.button_color,
    color: theme.UpdateTheme.text_color,
    "&:hover": {
      backgroundColor: theme.UpdateTheme.button_color,
      color: theme.UpdateTheme.text_color,
      opacity: 0.9, // subtle visual feedback
    },
  },
  chatButton: {
    backgroundColor: theme.UpdateTheme.button_color,
    color: theme.UpdateTheme.text_color,
    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
    "&:hover": {
      backgroundColor: theme.UpdateTheme.button_color,
      color: theme.UpdateTheme.text_color,
      opacity: 0.9,
    },
  },
}));

const ChatWidget = () => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const sendMessage = async () => {
    if (!userInput.trim()) return;

    setMessages((prev) => [...prev, { role: "user", content: userInput }]);
    setUserInput("");
    setLoading(true);

    try {
      const response = await fetch("https://iccapi.brihha.in/ask", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: userInput }),
      });

      const data = await response.json();
      const answerText = data.answer || "No response found.";
      const images = Array.isArray(data.images) ? data.images : [];

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: answerText, images },
      ]);
    } catch (error) {
      console.error("API Error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Something went wrong. Please try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  //const toggleChat = () => setOpen((prev) => !prev);

  const toggleChat = () => {
    setOpen((prev) => {
      if (prev) {       
        setMessages([]);
      }
      return !prev;
    });
  };

  const clearMessages = () => setMessages([]);

  return (
    <Box className={classes.container}>
      {open ? (
        <Paper elevation={4} className={classes.paper}>
          {/* Header */}
          <Box className={classes.header}>
            <Typography variant="subtitle1">
              <b>{t("learning-path.chat-assistant")}</b>
            </Typography>
            <Box>
              <IconButton
                onClick={clearMessages}
                size="small"
                aria-label="Clear messages"
                title="Clear messages"
                className={classes.headerIcon}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={toggleChat}
                size="small"
                aria-label="Close chat"
                title="Close chat"
                className={classes.headerIcon}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Chat Messages */}
          <Box className={classes.messageBox}>
            {messages.map((msg, i) => (
              <Box
                key={i}
                className={
                  msg.role === "user"
                    ? classes.userMessage
                    : classes.assistantMessage
                }
              >
                <Typography variant="body2" className={classes.messageText}>
                  {msg.content.replace(/\*/g, "")}
                </Typography>

                {/* Images */}
                {msg.images && msg.images.length > 0 && (
                  <Box mt={1}>
                    {msg.images.map((img, idx) => (
                      <Box key={idx} mt={1}>
                        <Typography
                          variant="caption"
                          className={classes.pdfLink}
                          onClick={() =>
                            window.open(
                              `data:application/pdf;base64,${img.image_base64}`,
                              "_blank"
                            )
                          }
                        >
                          {img.file}
                        </Typography>
                        <br />
                        <img
                          src={`data:image/jpeg;base64,${img.image_base64}`}
                          alt={img.file}
                          className={classes.imagePreview}
                        />
                        {img.page && (
                          <Typography
                            variant="caption"
                            className={classes.pageLabel}
                          >
                            Page: {img.page}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}

            {loading && (
              <Box className={classes.typingIndicator}>
                <Typography variant="body2">Generating...</Typography>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Input Field */}
          <Box className={classes.inputContainer}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Ask me anything"
              InputProps={{
                style: { height: 35, fontSize: 15 },
              }}
            />
            <Box ml={1}>
              <Button
                variant="contained"
                onClick={sendMessage}
                className={classes.sendButton}
              >
                {t("learning-path.send")}
              </Button>
            </Box>
          </Box>
        </Paper>
      ) : (
        <IconButton
          onClick={toggleChat}
          aria-label="Open chat"
          title="Open chat"
          className={classes.chatButton}
        >
          <ChatIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default ChatWidget;